// @ts-check
import { initSchema } from '@aws-amplify/datastore';
import { schema } from './schema';

const UserType = {
  "ROOT": "Root",
  "SUPER_ADMIN": "SuperAdmin",
  "CONTENT_ADMIN": "ContentAdmin",
  "BILLING_ADMIN": "BillingAdmin",
  "AI_ENGINEER": "AIEngineer",
  "USER": "User"
};

const AlertTriggerType = {
  "ANALYTICS": "ANALYTICS",
  "SYSTEM": "SYSTEM",
  "CUSTOM": "CUSTOM"
};

const AlertSeverity = {
  "INFO": "info",
  "WARNING": "warning",
  "CRITICAL": "critical"
};

const AlertConditionOperator = {
  "EQ": "eq",
  "NEQ": "neq",
  "GT": "gt",
  "GTE": "gte",
  "LT": "lt",
  "LTE": "lte",
  "CONTAINS": "contains",
  "NOT_CONTAINS": "not_contains"
};

const AuthorisationType = {
  "BEARER": "bearer",
  "APIKEY": "apikey",
  "OAUTH": "oauth",
  "PASSWORD": "password",
  "ENV": "env",
  "GENERIC": "generic",
  "NONE": "none",
  "USERNAME_AND_PASSWORD": "username_and_password",
  "BASIC": "basic"
};

const SearchStrategy = {
  "PARALLEL": "parallel",
  "TARGETED_FIRST": "targeted_first",
  "VECTOR_FIRST": "vector_first"
};

const ReasoningMode = {
  "AUTO": "auto",
  "DYNAMIC": "dynamic",
  "ALWAYS": "always",
  "NEVER": "never"
};

const ReasoningEffort = {
  "NONE": "none",
  "LOW": "low",
  "MEDIUM": "medium",
  "HIGH": "high"
};

const HostingType = {
  "AZURE_OPENAI": "azure_openai",
  "AWS": "aws",
  "LOCAL": "local",
  "GOOGLE": "google",
  "ANTHROPIC": "anthropic",
  "TOGETHER": "together",
  "TF": "tf",
  "REPLICATE": "replicate",
  "AZURE": "azure",
  "OPENAI": "openai",
  "GROQ": "groq",
  "OLLAMA": "ollama"
};

const FunctionRequestType = {
  "GET": "get",
  "POST": "post",
  "PUT": "put",
  "DELETE": "delete",
  "PATCH": "patch",
  "CUSTOM": "custom",
  "GRAPHQL_QUERY": "graphql_query",
  "GRAPHQL_MUTATION": "graphql_mutation"
};

const RequestType = {
  "TRANSLATOR": "translator",
  "ANSWERER": "answerer",
  "PARSER": "parser",
  "TOKENIZER": "tokenizer",
  "EMBEDDER": "embedder",
  "DETECTOR": "detector",
  "QUESTIONER": "questioner",
  "GENERATOR": "generator",
  "CHATTER": "chatter",
  "PLANNER": "planner",
  "ANALYSER": "analyser",
  "BROWSER": "browser",
  "VOICE": "voice",
  "TFCODE": "tfcode",
  "TFDESIGN": "tfdesign",
  "PROXY": "proxy",
  "VECTORISATION": "vectorisation",
  "IMAGE_EXTRACTION": "image_extraction",
  "SUPPORT": "support",
  "CODE_EXECUTION": "code_execution"
};

const OutputType = {
  "IMAGES": "images",
  "VIDEOS": "videos",
  "MODELLING": "modelling",
  "TEXT": "text",
  "CODE_INTERPRETER": "code_interpreter",
  "CANVAS": "canvas",
  "INTERNET_SEARCH": "internet_search",
  "DEEP_THINKING": "deep_thinking",
  "BROWSER": "browser",
  "AUDIO": "audio",
  "VOICE": "voice",
  "RAG": "rag"
};

const RequestStatus = {
  "SUCCESS": "success",
  "FAILED": "failed",
  "TIMEOUT": "timeout",
  "FULFILLED": "fulfilled",
  "PENDING": "pending",
  "IN_PROGRESS": "inProgress",
  "CANCELLED": "cancelled",
  "AWAITING_USER_INPUT": "awaitingUserInput",
  "CONTINUED": "continued",
  "INGESTED": "ingested"
};

const AgentType = {
  "CHATBOT": "Chatbot",
  "BUSINESS_ANALYST": "BusinessAnalyst",
  "CONTENT_CREATOR": "ContentCreator",
  "DEFAULT": "Default"
};

const ModelType = {
  "EXTRA_SMALL": "extraSmall",
  "SMALL": "small",
  "MEDIUM": "medium",
  "LARGE": "large",
  "EXTRA_LARGE": "extraLarge",
  "ULTRA": "ultra",
  "LOCAL": "local",
  "CUSTOM": "custom",
  "EXTERNAL": "external"
};

const SiteStatus = {
  "NO_DOMAIN": "noDomain",
  "NO_ANSWER": "noAnswer",
  "ACTIVE": "active",
  "SYNCING": "syncing",
  "INACTIVE": "inactive",
  "INVALID_TOKEN": "invalidToken",
  "FAILED": "failed",
  "PENDING": "pending",
  "IN_PROGRESS": "inProgress",
  "VALIDATION_TOKEN_CREATION_ERROR": "validationTokenCreationError",
  "READY_FOR_VALIDATION": "readyForValidation",
  "TOKEN_REQUESTED": "tokenRequested",
  "NO_SITEMAP": "noSitemap"
};

const FunctionScope = {
  "CUSTOMER_AUTHENTICATION": "customerAuthentication",
  "CUSTOMER": "customer",
  "CASE_AUTHENTICATION": "caseAuthentication",
  "CASE": "case"
};

const ChatVisibility = {
  "PUBLIC": "public",
  "PRIVATE": "private",
  "WIDGET": "widget",
  "JOB": "job",
  "INCOGNITO": "incognito"
};

const HumanFeedbackType = {
  "MULTILANGUAGE": "multilanguage",
  "DOCUMENTATION": "documentation",
  "REASONING": "reasoning",
  "IMAGES": "images",
  "CHARTING": "charting",
  "API": "api",
  "CODE": "code",
  "MATH": "math",
  "MODERATION": "moderation"
};

const MultiLanguageHumanFeedbackSubType = {
  "TRANSLATION": "translation",
  "LANGUAGE_DETECTION": "languageDetection",
  "LANGUAGE_DETECTION_AND_TRANSLATION": "languageDetectionAndTranslation"
};

const DocumentationHumanFeedbackSubType = {
  "OUTDATED_DOCUMENTATION": "OutdatedDocumentation",
  "MISSING_DOCUMENTATION": "MissingDocumentation",
  "INCORRECT_DOCUMENTATION": "IncorrectDocumentation"
};

const ReasoningHumanFeedbackSubType = {
  "INCORRECT_ANSWER": "IncorrectAnswer",
  "OUT_OF_CONTEXT_ANSWER": "OutOfContextAnswer"
};

const ImagesHumanFeedbackSubType = {
  "INCORRECT_IMAGE": "IncorrectImage",
  "MISSING_IMAGE": "MissingImage"
};

const ChartingHumanFeedbackSubType = {
  "INCORRECT_CHART": "IncorrectChart",
  "MISSING_CHART": "MissingChart"
};

const ModerationHumanFeedbackSubType = {
  "INCORRECT_MODERATION": "IncorrectModeration",
  "MISSING_MODERATION": "MissingModeration"
};

const AgentFunctionChatAction = {
  "SUGGESTION": "suggestion",
  "NEXT_QUESTION": "nextQuestion",
  "HUMAN_SUPPORT": "humanSupport",
  "REDIRECTION": "redirection",
  "SCRIPT": "script"
};

const CommunicationChannel = {
  "EMAIL": "email",
  "SMS": "sms",
  "WHATSAPP": "whatsapp",
  "LINKEDIN": "linkedin",
  "SIP": "sip",
  "TELEGRAM": "telegram"
};

const CommunicationProvider = {
  "SENDGRID": "sendgrid",
  "SES": "ses",
  "MAILGUN": "mailgun",
  "POSTMARK": "postmark",
  "MAILJET": "mailjet",
  "TWILIO": "twilio",
  "WHATSAPP": "whatsapp",
  "SMS_MAGIC": "sms_magic",
  "SMS_MAGIC_SF": "sms_magic_sf",
  "LINKEDIN": "linkedin",
  "TELEGRAM": "telegram"
};

const InternetSearchMode = {
  "SEARCH": "search",
  "NEWS": "news",
  "IMAGES": "images",
  "VIDEOS": "videos",
  "MAPS": "maps",
  "PLACES": "places",
  "SHOPPING": "shopping"
};

const VoucherStatus = {
  "ACTIVE": "ACTIVE",
  "INACTIVE": "INACTIVE"
};

const VoucherType = {
  "CREDIT": "CREDIT",
  "FREE_UPGRADE": "FREE_UPGRADE"
};

const ChunkingStrategy = {
  "KEYWORDS": "keywords",
  "SUMMARY": "summary"
};

const ExtractionStrategy = {
  "SAFE": "safe",
  "BRUTE": "brute"
};

const GenerationScopes = {
  "GENERATION": "generation",
  "TWEET": "tweet",
  "POST": "post",
  "TITLE": "title",
  "EMAIL": "email",
  "PARAPHRASE": "paraphrase",
  "PARAPHRASE_LITERAL": "paraphraseLiteral",
  "SUMMARY": "summary",
  "SIMPLIFY": "simplify",
  "CUSTOM": "custom"
};

const GenerationStyles = {
  "DEFAULT": "default",
  "SHORT": "short",
  "LONG": "long",
  "FUNNY": "funny",
  "INFORMAL": "informal",
  "FORMAL": "formal",
  "INTERVIEW": "interview",
  "ACADEMIC": "academic",
  "BUSINESS": "business",
  "CREATIVE": "creative",
  "SCIENTIFIC": "scientific",
  "POETRY": "poetry",
  "NEWS": "news"
};

const ChatMode = {
  "SPEED": "speed",
  "ACCURACY": "accuracy",
  "CUSTOM": "custom",
  "RETRIEVER": "retriever",
  "CODER": "coder",
  "MATHEMATICIAN": "mathematician",
  "CHATTER": "chatter",
  "PLANNER": "planner",
  "COMPUTER": "computer",
  "VOICE": "voice"
};

const GenerationMode = {
  "SPEED": "speed",
  "ACCURACY": "accuracy",
  "CUSTOM": "custom"
};

const HumanFeedback = {
  "POSITIVE": "positive",
  "NEGATIVE": "negative",
  "NEUTRAL": "neutral"
};

const MessageType = {
  "SYSTEM": "system",
  "HANDOFF": "handoff",
  "NOTIFICATION": "notification",
  "CONTINUATION": "continuation"
};

const GenerationDomains = {
  "EDITORIAL": "editorial",
  "CUSTOMER_SERVICE": "customerService",
  "SALES": "sales",
  "MARKETING": "marketing",
  "HR": "hr",
  "FINANCE": "finance",
  "LEGAL": "legal",
  "MEDICAL": "medical",
  "SCIENCE": "science",
  "TECHNOLOGY": "technology",
  "DEFAULT": "default"
};

const Department = {
  "HUMAN_RESOURCES": "HUMAN_RESOURCES",
  "FINANCE_AND_ACCOUNTING": "FINANCE_AND_ACCOUNTING",
  "SALES": "SALES",
  "MARKETING": "MARKETING",
  "OPERATIONS": "OPERATIONS",
  "INFORMATION_TECHNOLOGY": "INFORMATION_TECHNOLOGY",
  "CUSTOMER_SERVICE": "CUSTOMER_SERVICE",
  "RESEARCH_AND_DEVELOPMENT": "RESEARCH_AND_DEVELOPMENT",
  "LEGAL_AND_COMPLIANCE": "LEGAL_AND_COMPLIANCE",
  "SUPPLY_CHAIN_MANAGEMENT": "SUPPLY_CHAIN_MANAGEMENT",
  "PROJECT_MANAGEMENT": "PROJECT_MANAGEMENT",
  "GENERAL_PURPOSE": "GENERAL_PURPOSE"
};

const Visibility = {
  "PRIVATE": "private",
  "PUBLIC": "public",
  "SHARED": "shared"
};

const BaseModelType = {
  "PRODUCTIVITY": "productivity",
  "SENTIMENT": "sentiment",
  "NONE": "none"
};

const EntityAiType = {
  "NER": "ner",
  "INTENT": "intent",
  "TOPIC": "topic",
  "AGENT_GROUP": "agentGroup",
  "FUNCTION_GROUP": "functionGroup",
  "HOOK_GROUP": "hookGroup"
};

const FolderStatus = {
  "ACTIVE": "active",
  "INACTIVE": "inactive"
};

const WorkspaceAiType = {
  "HIERARCHICAL": "hierarchical",
  "FLAT": "flat"
};

const BatchLogType = {
  "NLP": "nlp",
  "RC": "rc",
  "GN": "gn",
  "BM": "bm",
  "ABM": "abm"
};

const SubscriptionStatus = {
  "ACTIVE": "active",
  "INACTIVE": "inactive"
};

const TrainingStatus = {
  "DISPATCHED": "dispatched",
  "IN_PROGRESS": "inProgress",
  "COMPLETED": "completed",
  "IN_ERROR": "inError",
  "CANCELLED": "cancelled",
  "CANCELLATION_REQUESTED": "cancellationRequested",
  "TRAINING": "training",
  "SWAPPING": "swapping",
  "SWAP_MODEL_FAILED": "swapModelFailed",
  "SWAP_MODEL_REQUESTED": "swapModelRequested",
  "DATASET_READY": "datasetReady",
  "MODEL_READY": "modelReady",
  "FINE_TUNING_REQUESTED": "fineTuningRequested"
};

const BatchJobStatus = {
  "DISPATCHED": "dispatched",
  "IN_PROGRESS": "inProgress",
  "COMPLETED": "completed",
  "IN_ERROR": "inError",
  "CANCELLED": "cancelled",
  "CANCELLATION_REQUESTED": "cancellationRequested"
};

const QaUrlStatus = {
  "READY": "ready",
  "NOT_READY": "notReady",
  "DISMISSED": "dismissed",
  "UNKNOWN": "unknown",
  "SWAPPING": "swapping"
};

const PlanType = {
  "BASE": "base",
  "PRO": "pro",
  "BUSINESS": "business",
  "ENTERPRISE": "enterprise"
};

const BillingProvider = {
  "STRIPE": "STRIPE",
  "XERO": "XERO",
  "NONE": "NONE"
};

const LogType = {
  "IMPORT": "import",
  "EXPORT": "export",
  "ENTITY_DELETION": "entityDeletion",
  "DOCUMENT_IMPORT": "documentImport"
};

const LogStatus = {
  "DISPATCHED": "dispatched",
  "IN_PROGRESS": "inProgress",
  "COMPLETED": "completed",
  "IN_ERROR": "inError"
};

const TrainingLogType = {
  "NLP_TRAINING": "nlpTraining",
  "READ_COMPREHENSION_TRAINING": "readComprehensionTraining",
  "GENERATIVE_TRAINING": "generativeTraining",
  "CONVERSATIONAL_TRAINING": "conversationalTraining",
  "GRPO_TRAINING": "grpoTraining",
  "KTO_TRAINING": "ktoTraining",
  "CONTINUED_PRETRAINING": "continuedPretraining"
};

const QaUrlType = {
  "SERVERLESS": "serverless",
  "PROVISIONED": "provisioned",
  "ELASTIC": "elastic"
};

const ReminderStatus = {
  "PENDING": "PENDING",
  "COMPLETED": "COMPLETED",
  "CANCELLED": "CANCELLED",
  "OVERDUE": "OVERDUE"
};

const ReminderPriority = {
  "LOW": "LOW",
  "MEDIUM": "MEDIUM",
  "HIGH": "HIGH",
  "URGENT": "URGENT"
};

const RecurringPattern = {
  "NONE": "NONE",
  "DAILY": "DAILY",
  "WEEKLY": "WEEKLY",
  "MONTHLY": "MONTHLY",
  "YEARLY": "YEARLY"
};

const ProjectStatus = {
  "PLANNING": "PLANNING",
  "ACTIVE": "ACTIVE",
  "ON_HOLD": "ON_HOLD",
  "COMPLETED": "COMPLETED",
  "CANCELLED": "CANCELLED",
  "ARCHIVED": "ARCHIVED"
};

const ItemType = {
  "TASK": "TASK",
  "MILESTONE": "MILESTONE",
  "ISSUE": "ISSUE",
  "RISK": "RISK",
  "DECISION": "DECISION",
  "DEPENDENCY": "DEPENDENCY",
  "DELIVERABLE": "DELIVERABLE"
};

const ItemStatus = {
  "TODO": "TODO",
  "IN_PROGRESS": "IN_PROGRESS",
  "BLOCKED": "BLOCKED",
  "COMPLETED": "COMPLETED",
  "ON_HOLD": "ON_HOLD",
  "CANCELLED": "CANCELLED",
  "PENDING": "PENDING",
  "ACHIEVED": "ACHIEVED",
  "DELAYED": "DELAYED",
  "OPEN": "OPEN",
  "RESOLVED": "RESOLVED",
  "REOPENED": "REOPENED",
  "IDENTIFIED": "IDENTIFIED",
  "MITIGATED": "MITIGATED",
  "ACCEPTED": "ACCEPTED",
  "AVOIDED": "AVOIDED"
};

const Priority = {
  "CRITICAL": "CRITICAL",
  "HIGH": "HIGH",
  "MEDIUM": "MEDIUM",
  "LOW": "LOW",
  "NONE": "NONE"
};

const CustomerType = {
  "INDIVIDUAL": "INDIVIDUAL",
  "COMPANY": "COMPANY",
  "GOVERNMENT": "GOVERNMENT",
  "NON_PROFIT": "NON_PROFIT",
  "RESELLER": "RESELLER",
  "PARTNER": "PARTNER"
};

const CustomerStatus = {
  "ACTIVE": "ACTIVE",
  "INACTIVE": "INACTIVE",
  "CHURNED": "CHURNED",
  "BLOCKED": "BLOCKED",
  "PENDING_REVIEW": "PENDING_REVIEW"
};

const CustomerLifecycle = {
  "LEAD": "LEAD",
  "PROSPECT": "PROSPECT",
  "QUALIFIED": "QUALIFIED",
  "NEGOTIATING": "NEGOTIATING",
  "CUSTOMER": "CUSTOMER",
  "REPEAT_CUSTOMER": "REPEAT_CUSTOMER",
  "ADVOCATE": "ADVOCATE",
  "CHURNED": "CHURNED"
};

const CustomerSegment = {
  "ENTERPRISE": "ENTERPRISE",
  "MID_MARKET": "MID_MARKET",
  "SMB": "SMB",
  "STARTUP": "STARTUP",
  "STRATEGIC": "STRATEGIC",
  "GROWTH": "GROWTH"
};

const PipelineStage = {
  "NEW_CONTACT": "NEW_CONTACT",
  "DISCOVERY": "DISCOVERY",
  "QUALIFICATION": "QUALIFICATION",
  "PROPOSAL": "PROPOSAL",
  "NEGOTIATION": "NEGOTIATION",
  "CLOSING": "CLOSING",
  "WON": "WON",
  "LOST": "LOST",
  "ON_HOLD": "ON_HOLD"
};

const AcquisitionSource = {
  "REFERRAL": "REFERRAL",
  "WEBSITE": "WEBSITE",
  "SOCIAL_MEDIA": "SOCIAL_MEDIA",
  "EVENT": "EVENT",
  "COLD_OUTREACH": "COLD_OUTREACH",
  "PARTNERSHIP": "PARTNERSHIP",
  "ADVERTISING": "ADVERTISING",
  "WORD_OF_MOUTH": "WORD_OF_MOUTH",
  "OTHER": "OTHER"
};

const RelationshipStrength = {
  "CHAMPION": "CHAMPION",
  "STRONG": "STRONG",
  "GOOD": "GOOD",
  "NEUTRAL": "NEUTRAL",
  "WEAK": "WEAK",
  "AT_RISK": "AT_RISK"
};

const CustomerLogType = {
  "SYSTEM": "SYSTEM",
  "USER": "USER",
  "AUTOMATION": "AUTOMATION",
  "INTEGRATION": "INTEGRATION",
  "API": "API"
};

const CustomerLogAction = {
  "CREATE": "CREATE",
  "UPDATE": "UPDATE",
  "DELETE": "DELETE",
  "STATUS_CHANGE": "STATUS_CHANGE",
  "LIFECYCLE_CHANGE": "LIFECYCLE_CHANGE",
  "PIPELINE_CHANGE": "PIPELINE_CHANGE",
  "EMAIL_SENT": "EMAIL_SENT",
  "EMAIL_RECEIVED": "EMAIL_RECEIVED",
  "CALL_MADE": "CALL_MADE",
  "CALL_RECEIVED": "CALL_RECEIVED",
  "MEETING_SCHEDULED": "MEETING_SCHEDULED",
  "MEETING_COMPLETED": "MEETING_COMPLETED",
  "NOTE_ADDED": "NOTE_ADDED",
  "DOCUMENT_UPLOADED": "DOCUMENT_UPLOADED",
  "ASSIGNMENT_CHANGED": "ASSIGNMENT_CHANGED",
  "REVENUE_UPDATED": "REVENUE_UPDATED",
  "OPPORTUNITY_CREATED": "OPPORTUNITY_CREATED",
  "CUSTOM_ACTION": "CUSTOM_ACTION"
};

const CronJobStatus = {
  "ACTIVE": "ACTIVE",
  "PAUSED": "PAUSED",
  "DISABLED": "DISABLED",
  "RUNNING": "RUNNING",
  "SENSING": "SENSING",
  "PLANNING": "PLANNING",
  "ACTING": "ACTING",
  "COMPLETED": "COMPLETED",
  "FAILED": "FAILED",
  "PENDING": "PENDING"
};

const CronJobType = {
  "TASK": "TASK",
  "ROUTINE": "ROUTINE"
};

const CronJobFrequency = {
  "MINUTES": "MINUTES",
  "HOURLY": "HOURLY",
  "DAILY": "DAILY",
  "WEEKLY": "WEEKLY",
  "MONTHLY": "MONTHLY",
  "YEARLY": "YEARLY",
  "CUSTOM": "CUSTOM"
};

const TriggerEventSource = {
  "NATIVE": "NATIVE",
  "EXTERNAL": "EXTERNAL"
};

const TriggerStatus = {
  "ARMED": "ARMED",
  "PAUSED": "PAUSED",
  "DISABLED": "DISABLED",
  "FIRING": "FIRING",
  "COMPLETED": "COMPLETED",
  "FAILED": "FAILED",
  "PENDING": "PENDING"
};

const TriggerAcquisitionMode = {
  "POLLING": "POLLING",
  "WEBHOOK": "WEBHOOK"
};

const TriggerProvider = {
  "GOOGLE_DRIVE": "googleDrive",
  "MICROSOFT_GRAPH": "microsoftGraph",
  "SLACK": "slack",
  "GITHUB": "github",
  "NOTION": "notion",
  "DROPBOX": "dropbox",
  "ONEDRIVE": "onedrive",
  "BOX": "box",
  "SALESFORCE": "salesforce",
  "HUBSPOT": "hubspot",
  "LINEAR": "linear",
  "JIRA": "jira",
  "ASANA": "asana",
  "TRELLO": "trello",
  "ZENDESK": "zendesk",
  "SERVICENOW": "servicenow",
  "YOUTUBE": "youtube",
  "GMAIL": "gmail",
  "CALENDAR": "calendar",
  "FORMS": "forms",
  "TEAMS": "teams",
  "STRIPE": "stripe",
  "GENERIC": "generic"
};

const EntitySnapshotType = {
  "AGENT": "AGENT",
  "AGENT_FUNCTION": "AGENT_FUNCTION",
  "CUSTOM_CODE_ENV": "CUSTOM_CODE_ENV"
};

const { AppConfig, User, Workspace, UserPolicy, UserWorkspaceType, Agent, AgentFunction, Authorisation, Hosting, MCP, DatabaseConnection, UserNotification, Alert, VirtualMachine, CustomExecutionEnvironment, Site, EntityAI, TrainingLog, BatchJobLog, AppLog, RequestLog, OutputStream, Folders, DictionaryItem, Chat, CustomPrompt, CronJob, Trigger, CustomCodeExecutionEnvironment, ChatMessage, CommunicationService, SubscriptionPricingPlan, Voucher, VoucherRedemption, SubscriptionBaseConfig, GenerationConfig, ArenaBattle, Benchmark, Project, Reminder, ProjectItem, Customer, Log, EntitySnapshot, TriggerNativeConfig, TriggerExternalConfig, TriggerPollConfig, TriggerWebhookConfig, RoutineConfig, ChatStatus, ChannelSettings, ChannelConfig, CustomGenerationConfig, QaRole, CronSchedule, CronExecutionConfig, CronNotificationConfig, CronRetryConfig, CustomQaConfig, AiWorkspaceConsumption, ApiMethodConsumption, Prompt, BenchmarkItem, ConfigMapByPlan, UserWorkspace, TrainingConfig, ParsingConfig, NotificationConfig, EmbeddingConfig, ModelDocumentFilterInput, QA, Document, MinimalChatUpdate } = initSchema(schema);

export {
  AppConfig,
  User,
  Workspace,
  UserPolicy,
  UserWorkspaceType,
  Agent,
  AgentFunction,
  Authorisation,
  Hosting,
  MCP,
  DatabaseConnection,
  UserNotification,
  Alert,
  VirtualMachine,
  CustomExecutionEnvironment,
  Site,
  EntityAI,
  TrainingLog,
  BatchJobLog,
  AppLog,
  RequestLog,
  OutputStream,
  Folders,
  DictionaryItem,
  Chat,
  CustomPrompt,
  CronJob,
  Trigger,
  CustomCodeExecutionEnvironment,
  ChatMessage,
  CommunicationService,
  SubscriptionPricingPlan,
  Voucher,
  VoucherRedemption,
  SubscriptionBaseConfig,
  GenerationConfig,
  ArenaBattle,
  Benchmark,
  Project,
  Reminder,
  ProjectItem,
  Customer,
  Log,
  EntitySnapshot,
  UserType,
  AlertTriggerType,
  AlertSeverity,
  AlertConditionOperator,
  AuthorisationType,
  SearchStrategy,
  ReasoningMode,
  ReasoningEffort,
  HostingType,
  FunctionRequestType,
  RequestType,
  OutputType,
  RequestStatus,
  AgentType,
  ModelType,
  SiteStatus,
  FunctionScope,
  ChatVisibility,
  HumanFeedbackType,
  MultiLanguageHumanFeedbackSubType,
  DocumentationHumanFeedbackSubType,
  ReasoningHumanFeedbackSubType,
  ImagesHumanFeedbackSubType,
  ChartingHumanFeedbackSubType,
  ModerationHumanFeedbackSubType,
  AgentFunctionChatAction,
  CommunicationChannel,
  CommunicationProvider,
  InternetSearchMode,
  VoucherStatus,
  VoucherType,
  ChunkingStrategy,
  ExtractionStrategy,
  GenerationScopes,
  GenerationStyles,
  ChatMode,
  GenerationMode,
  HumanFeedback,
  MessageType,
  GenerationDomains,
  Department,
  Visibility,
  BaseModelType,
  EntityAiType,
  FolderStatus,
  WorkspaceAiType,
  BatchLogType,
  SubscriptionStatus,
  TrainingStatus,
  BatchJobStatus,
  QaUrlStatus,
  PlanType,
  BillingProvider,
  LogType,
  LogStatus,
  TrainingLogType,
  QaUrlType,
  ReminderStatus,
  ReminderPriority,
  RecurringPattern,
  ProjectStatus,
  ItemType,
  ItemStatus,
  Priority,
  CustomerType,
  CustomerStatus,
  CustomerLifecycle,
  CustomerSegment,
  PipelineStage,
  AcquisitionSource,
  RelationshipStrength,
  CustomerLogType,
  CustomerLogAction,
  CronJobStatus,
  CronJobType,
  CronJobFrequency,
  TriggerEventSource,
  TriggerStatus,
  TriggerAcquisitionMode,
  TriggerProvider,
  EntitySnapshotType,
  TriggerNativeConfig,
  TriggerExternalConfig,
  TriggerPollConfig,
  TriggerWebhookConfig,
  RoutineConfig,
  ChatStatus,
  ChannelSettings,
  ChannelConfig,
  CustomGenerationConfig,
  QaRole,
  CronSchedule,
  CronExecutionConfig,
  CronNotificationConfig,
  CronRetryConfig,
  CustomQaConfig,
  AiWorkspaceConsumption,
  ApiMethodConsumption,
  Prompt,
  BenchmarkItem,
  ConfigMapByPlan,
  UserWorkspace,
  TrainingConfig,
  ParsingConfig,
  NotificationConfig,
  EmbeddingConfig,
  ModelDocumentFilterInput,
  QA,
  Document,
  MinimalChatUpdate
};